import { z } from "zod";
export * from './notifications.js';

export const RoleSchema = z.enum(["administrator", "manager", "contractor"]);
export type Role = z.infer<typeof RoleSchema>;

export const SnagStatusSchema = z.enum([
  "open",
  "in_progress",
  "ready_to_inspect",
  "closed",
]);
export type SnagStatus = z.infer<typeof SnagStatusSchema>;

export const EvidenceKindSchema = z.enum([
  "issue",
  "completion",
  "manager_close_off",
  "issue_video",
  "comment_attachment",
]);
export type EvidenceKind = z.infer<typeof EvidenceKindSchema>;

export const MAX_COMMENT_ATTACHMENTS = 6;
export const MAX_COMMENT_FILE_BYTES = 20 * 1024 * 1024;
export const COMMENT_FILE_TYPES: Record<string, string> = {
  'image/jpeg': '.jpg', 'image/png': '.png', 'image/webp': '.webp', 'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'text/plain': '.txt',
};
export type CommentAttachment = { id: string; fileName: string; contentType: string; sizeBytes: number };
export type SnagComment = { id: string; actorId: string | null; actorName: string; actorRole: string; createdAt: string; text: string; attachments?: CommentAttachment[] };
export const AddCommentSchema = z.object({
  text: z.string().trim().max(4000).default(''), clientId: z.string().uuid(),
  attachmentIds: z.array(z.string().uuid()).max(MAX_COMMENT_ATTACHMENTS).default([]),
}).refine(value => Boolean(value.text || value.attachmentIds.length), 'Add a comment or file')
  .refine(value => new Set(value.attachmentIds).size === value.attachmentIds.length, 'Each file can only be attached once');
export type AddComment = { text: string; clientId: string; attachmentIds?: string[] };

export const SyncStateSchema = z.enum([
  "saved_on_device",
  "uploading",
  "synced",
  "needs_attention",
]);
export type SyncState = z.infer<typeof SyncStateSchema>;

export const UserSchema = z.object({
  id: z.string().uuid(),
  organisationId: z.string().uuid(),
  contractorCompanyId: z.string().uuid().nullable(),
  name: z.string().min(1).max(120),
  email: z.email(),
  telephone: z.string().max(40),
  role: RoleSchema,
  active: z.boolean(),
  mustChangePassword: z.boolean().default(false),
  createdAt: z.iso.datetime().optional(),
  archivedAt: z.iso.datetime().nullable().optional(),
});
export type User = z.infer<typeof UserSchema>;

export const ProjectSchema = z.object({
  id: z.string().uuid(),
  organisationId: z.string().uuid(),
  name: z.string().min(1).max(160),
  phase: z.string().max(160),
  address: z.string().max(500),
  jobReference: z.string().max(32),
  createdBy: z.string().uuid().nullable(),
  managerAccessAll: z.boolean(),
  status: z.enum(["running", "archived"]),
  updatedAt: z.iso.datetime(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const CreateProjectSchema = z.object({
  name: z.string().trim().min(1).max(160),
  phase: z.string().trim().max(160).default(""),
  address: z.string().trim().max(500).default(""),
  jobReference: z.string().trim().max(32).default(""),
  managerAccessAll: z.boolean().optional(),
  managerIds: z.array(z.string().uuid()).max(100).optional(),
});
export type CreateProject = z.infer<typeof CreateProjectSchema>;

export const ContractorCompanySchema = z.object({
  id: z.string().uuid(),
  organisationId: z.string().uuid(),
  company: z.string().min(1).max(160),
  trade: z.string().min(1).max(120),
  contactName: z.string().max(120),
  telephone: z.string().max(40),
  email: z.union([z.literal(""), z.email()]),
  accountCount: z.number().int().nonnegative(),
  fullSnagListReadOnly: z.boolean(),
  active: z.boolean(),
});
export type ContractorCompany = z.infer<typeof ContractorCompanySchema>;

export const CreateContractorSchema = z.object({
  company: z.string().trim().min(1).max(160),
  trade: z.string().trim().min(1).max(120),
  contactName: z.string().trim().max(120).default(""),
  telephone: z.string().trim().max(40).default(""),
  email: z.union([z.literal(""), z.email()]).default(""),
});
export type CreateContractor = z.infer<typeof CreateContractorSchema>;

export const LocationSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string().min(1).max(160),
  active: z.boolean(),
});
export type Location = z.infer<typeof LocationSchema>;

export const DrawingSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  title: z.string().min(1).max(200),
  revision: z.string().max(80),
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1).max(120),
  sizeBytes: z.number().int().nonnegative(),
  superseded: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type Drawing = z.infer<typeof DrawingSchema>;

export const EvidenceSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  projectId: z.string().uuid(),
  snagId: z.string().uuid(),
  kind: EvidenceKindSchema,
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1).max(120),
  sizeBytes: z.number().int().nonnegative(),
  checksumSha256: z.string().length(64),
  capturedAt: z.iso.datetime(),
  uploadedAt: z.iso.datetime(),
  uploadedBy: z.string().uuid().nullable(),
});
export type Evidence = z.infer<typeof EvidenceSchema>;

export const CreateLocationSchema = z.object({
  name: z.string().trim().min(1).max(160),
});
export type CreateLocation = z.infer<typeof CreateLocationSchema>;

export const InviteContractorSchema = z.object({
  contractorCompanyId: z.string().uuid(),
  kind: z.enum(["company", "individual"]).default("company"),
  firstName: z.string().trim().max(60).default(""),
  lastName: z.string().trim().max(60).default(""),
  email: z.email().optional(),
  telephone: z.string().trim().max(40).default(""),
}).superRefine((value, context) => {
  if (value.kind === "individual" && !value.firstName) {
    context.addIssue({ code: "custom", path: ["firstName"], message: "A first name is required for an individual invitation" });
  }
  if (value.kind === "individual" && !value.lastName) {
    context.addIssue({ code: "custom", path: ["lastName"], message: "A last name is required for an individual invitation" });
  }
});
export type InviteContractor = z.infer<typeof InviteContractorSchema>;

export const InviteManagerSchema = z.object({
  email: z.email(),
});
export type InviteManager = z.infer<typeof InviteManagerSchema>;

export const InvitationSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid().nullable(),
  contractorCompanyId: z.string().uuid().nullable(),
  email: z.email().nullable(),
  firstName: z.string().max(60),
  lastName: z.string().max(60),
  telephone: z.string().max(40),
  role: z.enum(["manager", "contractor"]),
  kind: z.enum(["company", "individual"]),
  expiresAt: z.iso.datetime(),
  acceptedAt: z.iso.datetime().nullable(),
  revokedAt: z.iso.datetime().nullable(),
  useCount: z.number().int().nonnegative(),
});
export type Invitation = z.infer<typeof InvitationSchema>;

export const InvitationPreviewSchema = z.object({
  projectId: z.string().uuid().nullable(),
  contractorCompanyId: z.string().uuid().nullable(),
  company: z.string().max(160).nullable(),
  trade: z.string().max(120).nullable(),
  email: z.email().nullable(),
  firstName: z.string().max(60),
  lastName: z.string().max(60),
  telephone: z.string().max(40),
  role: z.enum(["manager", "contractor"]),
  kind: z.enum(["company", "individual"]),
  expiresAt: z.iso.datetime(),
  useCount: z.number().int().nonnegative(),
});
export type InvitationPreview = z.infer<typeof InvitationPreviewSchema>;

export const PASSWORD_RULES = "Use at least 10 characters, including a special character such as !, @ or #.";
export const NewPasswordSchema = z.string().min(10, PASSWORD_RULES).max(200)
  .regex(/[^\p{L}\p{N}\s]/u, PASSWORD_RULES);

export const AcceptInvitationSchema = z.object({
  token: z.string().min(32).max(512),
  name: z.string().trim().min(1).max(120),
  email: z.email().optional(),
  password: NewPasswordSchema,
  deviceId: z.string().uuid(),
  deviceName: z.string().min(1).max(160),
});
export type AcceptInvitation = z.infer<typeof AcceptInvitationSchema>;

export const RefreshSessionSchema = z.object({
  refreshToken: z.string().min(1),
});
export type RefreshSession = z.infer<typeof RefreshSessionSchema>;

export const SnagSchema = z.object({
  id: z.string().uuid(),
  organisationId: z.string().uuid(),
  projectId: z.string().uuid(),
  locationId: z.string().uuid().nullable(),
  contractorCompanyId: z.string().uuid().nullable(),
  drawingId: z.string().uuid().nullable(),
  drawingPage: z.number().int().positive().nullable(),
  markupX: z.number().min(0).max(1).nullable(),
  markupY: z.number().min(0).max(1).nullable(),
  pinCoordinateSpace: z.enum(["image", "legacy"]).optional(),
  reference: z.string().min(1).max(32),
  title: z.string().min(1).max(240),
  description: z.string().max(4000),
  trade: z.string().min(1).max(120),
  priority: z.enum(["low", "normal", "high", "urgent"]),
  status: SnagStatusSchema,
  dueDate: z.iso.date().nullable(),
  completionNote: z.string().max(4000),
  managerNote: z.string().max(4000),
  recordState: z.enum(["live", "archived", "deleted"]),
  recordStateChangedAt: z.iso.datetime().nullable(),
  recordStateChangedBy: z.string().uuid().nullable(),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  version: z.number().int().positive(),
});
export type Snag = z.infer<typeof SnagSchema>;

export const CreateSnagSchema = z.object({
  clientId: z.string().uuid(),
  projectId: z.string().uuid(),
  locationId: z.string().uuid().nullable().default(null),
  contractorCompanyId: z.string().uuid().nullable().default(null),
  drawingId: z.string().uuid().nullable().default(null),
  drawingPage: z.number().int().positive().nullable().default(null),
  markupX: z.number().min(0).max(1).nullable().default(null),
  markupY: z.number().min(0).max(1).nullable().default(null),
  pinCoordinateSpace: z.enum(["image", "legacy"]).optional(),
  title: z.string().trim().min(1).max(240),
  description: z.string().trim().max(4000).default(""),
  trade: z.string().trim().min(1).max(120).default("General"),
  priority: z.enum(["low", "normal", "high", "urgent"]).default("normal"),
  dueDate: z.iso.date().nullable().default(null),
  capturedAt: z.iso.datetime(),
});
export type CreateSnag = z.infer<typeof CreateSnagSchema>;

export const ChangeStatusSchema = z.object({
  clientId: z.string().uuid(),
  status: SnagStatusSchema,
  note: z.string().trim().max(4000).default(""),
  expectedVersion: z.number().int().positive(),
  occurredAt: z.iso.datetime(),
});
export type ChangeStatus = z.infer<typeof ChangeStatusSchema>;

export const SyncOperationSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("create_snag"),
    operationId: z.string().uuid(),
    payload: CreateSnagSchema,
  }),
  z.object({
    kind: z.literal("change_status"),
    operationId: z.string().uuid(),
    snagId: z.string().uuid(),
    payload: ChangeStatusSchema,
  }),
]);
export type SyncOperation = z.infer<typeof SyncOperationSchema>;

export const SyncPushSchema = z.object({
  deviceId: z.string().uuid(),
  operations: z.array(SyncOperationSchema).max(100),
});
export type SyncPush = z.infer<typeof SyncPushSchema>;

export const SyncResultSchema = z.object({
  operationId: z.string().uuid(),
  status: z.enum(["applied", "already_applied", "conflict", "rejected"]),
  entityId: z.string().uuid().nullable(),
  serverVersion: z.number().int().positive().nullable(),
  message: z.string().max(500),
});
export type SyncResult = z.infer<typeof SyncResultSchema>;

export const LoginSchema = z.object({
  email: z.email(),
  password: z.string().min(8).max(200),
  deviceId: z.string().uuid(),
  deviceName: z.string().min(1).max(160),
});
export type Login = z.infer<typeof LoginSchema>;

export const SessionSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  expiresInSeconds: z.number().int().positive(),
  user: UserSchema,
});
export type Session = z.infer<typeof SessionSchema>;

export function cleanTranscript(value: string): string {
  return value.replace(/\[[^\]]*\]|\([^)]*\)/g, " ").replace(/\s+/g, " ").trim();
}

function collapseAdjacentSpeechLoops(value: string): string {
  let words = value.split(/\s+/).filter(Boolean);
  let changed = true;
  while (changed) {
    changed = false;
    for (let index = 0; index + 2 < words.length; index += 1) {
      const first = words[index]?.toLowerCase();
      if (first && first === words[index + 1]?.toLowerCase() && first === words[index + 2]?.toLowerCase()) {
        words.splice(index + 1, 2);
        changed = true;
        break;
      }
    }
    if (changed) continue;
    outer: for (let start = 0; start < words.length; start += 1) {
      const largest = Math.min(32, Math.floor((words.length - start) / 2));
      for (let size = largest; size >= 2; size -= 1) {
        const first = words.slice(start, start + size).join(" ").toLowerCase();
        const second = words.slice(start + size, start + size * 2).join(" ").toLowerCase();
        if (first === second) {
          words = [...words.slice(0, start + size), ...words.slice(start + size * 2)];
          changed = true;
          break outer;
        }
      }
    }
  }
  // Windows speech recognition can emit its changing partial word as several
  // final tokens (for example "de dec decor decoration"). Keep only the
  // completed word when there is a clear chain of progressively longer
  // prefixes, without touching normal two-word phrases.
  for (let index = 0; index < words.length - 1;) {
    let end = index;
    while (end + 1 < words.length) {
      const current = words[end]!.toLowerCase().replace(/[^a-z]/g, "");
      const next = words[end + 1]!.toLowerCase().replace(/[^a-z]/g, "");
      if (current.length < 2 || next.length <= current.length || !next.startsWith(current)) break;
      end += 1;
    }
    const chainLength = end - index + 1;
    if (chainLength >= 3 || (chainLength === 2 && words[index]!.replace(/[^a-z]/gi, "").length <= 3)) {
      words.splice(index, chainLength - 1);
    } else {
      index = end + 1;
    }
  }
  return words.join(" ");
}

/** Joins final speech segments while removing the overlap returned by streaming engines. */
export function mergeTranscriptWithoutRepetition(existing: string, incoming: string): string {
  const left = collapseAdjacentSpeechLoops(cleanTranscript(existing));
  const right = collapseAdjacentSpeechLoops(cleanTranscript(incoming));
  if (!left) return right;
  if (!right || left.toLowerCase().endsWith(right.toLowerCase())) return left;
  if (right.toLowerCase().startsWith(`${left.toLowerCase()} `)) return right;
  if (left.toLowerCase().startsWith(`${right.toLowerCase()} `)) return left;

  const leftWords = left.split(/\s+/);
  const rightWords = right.split(/\s+/);
  const maxOverlap = Math.min(leftWords.length, rightWords.length, 64);
  for (let size = maxOverlap; size > 0; size -= 1) {
    const tail = leftWords.slice(-size).join(" ").toLowerCase();
    const head = rightWords.slice(0, size).join(" ").toLowerCase();
    if (tail === head) return collapseAdjacentSpeechLoops([...leftWords, ...rightWords.slice(size)].join(" "));
  }
  return collapseAdjacentSpeechLoops(`${left} ${right}`);
}
export * from './platform.js';
